import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  Button
} from '@mui/material';

import { useSimulation } from '../context/SimulationContext';
import { SimulationService } from '../services/simulation/simulationService';
import { RideSchema, FleetSettings, TimeWindowSettings as TimeWindowSettingsType, SimulationResults, FleetPresetType } from '../types/simulation';
import { applyFleetPreset } from '../services/simulation/syntheticData';
import { RideUploader } from '../components/simulation/RideUploader';
import { VehicleCreator } from '../components/simulation/VehicleCreator';
import { TimeWindowSettings } from '../components/simulation/TimeWindowSettings';
import { SimulationRunner } from '../components/simulation/SimulationRunner';

import { Icon } from '@liftango/ui-icons';
function SimulationPage() {
  const { t } = useTranslation('simulation');
  const steps = [
    t('setup.steps.upload'),
    t('setup.steps.fleet'),
    t('setup.steps.time'),
    t('setup.steps.run'),
  ];
  const [activeStep, setActiveStep] = useState(0);
  const [rides, setRides] = useState<RideSchema[]>([]);
  const [fleet, setFleet] = useState<FleetSettings | null>(null);
  const [timeSettings, setTimeSettings] = useState<TimeWindowSettingsType | null>(null);
  const [allSimulationResults, setAllSimulationResults] = useState<SimulationResults[]>([]);
  const [selectedFleetPresets, setSelectedFleetPresets] = useState<FleetPresetType[]>([]);
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  
  // Use existing simulation context
  const simulationContext = useSimulation();

  const handleRidesUploaded = (uploadedRides: RideSchema[]) => {
    setRides(uploadedRides);
    simulationContext.setNumberOfRides(uploadedRides.length);
  };

  const handleFleetCreated = (createdFleet: FleetSettings) => {
    setFleet(createdFleet);
    const totalVehicles = createdFleet.vehicles.reduce((sum, vehicle) => sum + vehicle.number_of_vehicles, 0);
    simulationContext.setNumberOfVehicles(totalVehicles);
  };

  const handleTimeSettingsChanged = (settings: TimeWindowSettingsType) => {
    setTimeSettings(settings);
  };

  const handleSimulationResult = (result: SimulationResults) => {
    setAllSimulationResults(prev => [...prev, result]);
  };

  const handleClearResults = () => {
    setAllSimulationResults([]);
  };

  // Preset management functions
  const handleFleetPresetSelectionChange = (selectedPresets: FleetPresetType[]) => {
    setSelectedFleetPresets(selectedPresets);
  };

  const handlePresetSelectionChange = (selectedPresets: string[]) => {
    setSelectedPresets(selectedPresets);
  };

  const handleStepClick = (step: number) => {
    if (step === 0) {
      setActiveStep(0);
    } else if (step === 1 && rides.length > 0) {
      setActiveStep(1);
    } else if (step === 2 && rides.length > 0 && fleet && fleet.vehicles.length > 0) {
      setActiveStep(2);
    } else if (step === 3 && rides.length > 0 && fleet && fleet.vehicles.length > 0 && timeSettings) {
      setActiveStep(3);
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const canNavigateNext = () => {
    switch (activeStep) {
      case 0:
        return rides.length > 0;
      case 1:
        return rides.length > 0 && fleet && fleet.vehicles.length > 0;
      case 2:
        return rides.length > 0 && fleet && fleet.vehicles.length > 0 && timeSettings;
      case 3:
        return false; // Last step
      default:
        return false;
    }
  };

  const canNavigatePrevious = () => {
    return activeStep > 0;
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <RideUploader
            onRidesUploaded={handleRidesUploaded}
            rides={rides}
          />
        );
      case 1:
        return (
          <VehicleCreator
            onFleetCreated={handleFleetCreated}
            fleet={fleet}
            rides={rides}
            selectedFleetPresets={selectedFleetPresets}
            onFleetPresetSelectionChange={handleFleetPresetSelectionChange}
          />
        );
      case 2:
        return (
          <TimeWindowSettings
            onSettingsChange={handleTimeSettingsChanged}
            onPresetSelectionChange={handlePresetSelectionChange}
            settings={timeSettings}
          />
        );
      case 3:
        return (
          <SimulationRunner
            rides={rides}
            fleet={fleet}
            timeSettings={timeSettings}
            allSimulationResults={allSimulationResults}
            onSimulationResult={handleSimulationResult}
            selectedPresets={selectedPresets}
            onPresetSelectionChange={handlePresetSelectionChange}
            selectedFleetPresets={selectedFleetPresets}
            onFleetPresetSelectionChange={handleFleetPresetSelectionChange}
            onClearResults={handleClearResults}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('setup.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('setup.subtitle')}
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => {
              const isClickable = 
                index === 0 || 
                (index === 1 && rides.length > 0) || 
                (index === 2 && rides.length > 0 && fleet && fleet.vehicles.length > 0) ||
                (index === 3 && rides.length > 0 && fleet && fleet.vehicles.length > 0 && timeSettings);

              return (
                <Step 
                  key={label} 
                  sx={{ 
                    cursor: isClickable ? 'pointer' : 'default',
                    opacity: isClickable ? 1 : 0.5 
                  }}
                  onClick={() => isClickable && handleStepClick(index)}
                >
                  <StepLabel>
                    <Typography variant="subtitle1">{label}</Typography>
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Box sx={{ mb: 4 }}>
          {renderStepContent(activeStep)}
          
          {/* Navigation Buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 3,
            pt: 2,
            borderTop: 1,
            borderColor: 'divider'
          }}>
            <Button
              variant="outlined"
              startIcon={<Icon size={20} name="arrow-left" />}
              onClick={handlePrevious}
              disabled={!canNavigatePrevious()}
              sx={{ minWidth: 120 }}
            >
              {t('setup.navigation.previous')}
            </Button>

            <Typography variant="body2" color="text.secondary">
              {t('setup.navigation.stepCounter', { current: activeStep + 1, total: steps.length, label: steps[activeStep] })}
            </Typography>

            <Button
              variant="contained"
              endIcon={<Icon size={20} name="arrow-right" />}
              onClick={handleNext}
              disabled={!canNavigateNext()}
              sx={{ minWidth: 120 }}
            >
              {t('setup.navigation.next')}
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', pt: 4, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('setup.footer.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('setup.footer.subtitle')}
          </Typography>
        </Box>
      </Container>
  );
}

export default SimulationPage;
